import { useEffect, useState } from "react";
import { useLocation } from "react-router";
import { toast } from "sonner";

import { IconNotification } from "@/components/notifications/notifications";
import { Select } from "@/components/ui/select/select";
import { TextArea } from "@/components/ui/textarea/textarea";
import {
	PROBLEM_REPORT_CATEGORIES,
	type ProblemReportCategory,
} from "@/features/feedback/types";
import { GeneralModal } from "@/features/modals/general-modal";
import {
	captureProblemReported,
	isProblemReportDescriptionValid,
} from "@/utils/problem-report-analytics";

interface ReportProblemModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export const ReportProblemModal = ({
	isOpen,
	onClose,
}: ReportProblemModalProps) => {
	const { pathname, search } = useLocation();
	const [category, setCategory] = useState<ProblemReportCategory>("bug");
	const [description, setDescription] = useState("");
	const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!isOpen) return;

		queueMicrotask(() => {
			setCategory("bug");
			setDescription("");
			setHasAttemptedSubmit(false);
			setIsSubmitting(false);
		});
	}, [isOpen]);

	const isDescriptionInvalid =
		hasAttemptedSubmit && !isProblemReportDescriptionValid(description);

	const handleClose = () => {
		setCategory("bug");
		setDescription("");
		setHasAttemptedSubmit(false);
		onClose();
	};

	const handleSubmit = () => {
		setHasAttemptedSubmit(true);

		if (!isProblemReportDescriptionValid(description) || isSubmitting) {
			return;
		}

		setIsSubmitting(true);

		const didCapture = captureProblemReported({
			category,
			description,
			pagePath: pathname,
			pageUrl: `${pathname}${search}`,
		});

		setIsSubmitting(false);

		if (!didCapture) {
			return;
		}

		toast.custom((t) => (
			<IconNotification
				color="success"
				description="Our team can review your session replay if recording was enabled."
				hideDismissLabel
				onClose={() => toast.dismiss(t)}
				title="Problem report submitted"
			/>
		));
		handleClose();
	};

	return (
		<GeneralModal
			closeOnSubmit={false}
			description="Describe what went wrong. Do not include passwords, API keys, personal data, or other sensitive information."
			isOpen={isOpen}
			onClose={handleClose}
			onSubmit={handleSubmit}
			submitDisabled={isSubmitting}
			submitText={isSubmitting ? "Submitting..." : "Submit report"}
			title="Report a problem"
		>
			<div className="flex flex-col gap-5">
				<Select
					items={[...PROBLEM_REPORT_CATEGORIES]}
					label="What kind of problem is this?"
					onChange={(key) => {
						if (typeof key === "string") {
							setCategory(key as ProblemReportCategory);
						}
					}}
					value={category}
				>
					{(item) => (
						<Select.Item id={item.id} key={item.id}>
							{item.label}
						</Select.Item>
					)}
				</Select>

				<TextArea
					errorMessage={
						isDescriptionInvalid
							? "Please enter at least 15 characters."
							: undefined
					}
					hint="Include what you were trying to do and what happened instead."
					isInvalid={isDescriptionInvalid}
					isRequired
					label="What happened?"
					onChange={setDescription}
					placeholder="Example: Export modal stays on step 1 after clicking Continue."
					rows={5}
					value={description}
				/>
			</div>
		</GeneralModal>
	);
};
